/**
 * Totales del ticket en el cliente.
 *
 * **Esto es una vista previa, no la verdad.** El total definitivo lo calcula el
 * servidor (ADR-0003): el POS manda qué productos y cuántas cantidades, y el
 * servidor responde con el importe. Si hubiera una sola diferencia de redondeo
 * entre estas dos implementaciones, la que manda es la del servidor.
 *
 * Existe porque el cajero necesita ver el total **antes** de cobrar, y porque
 * funciona sin conexión. La lógica está copiada del motor de impuestos del
 * backend (`app/modules/catalog/taxes.py`) y hay tests con los mismos números
 * para que no se separen.
 */

export interface TaxSpec {
  code: string;
  rate: number;
  satCode: string;
  satFactor: string;
}

export interface LineTotals {
  quantity: number;
  unitPriceCents: number;
  discountCents: number;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
}

export interface CartTotals {
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  totalCents: number;
  lineCount: number;
  itemCount: number;
}

/** Redondeo al alza en el medio centavo, igual que en el backend. */
function round(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value));
}

/** Importe de una línea: precio × cantidad, redondeado una sola vez. */
export function multiplyCents(cents: number, quantity: number): number {
  return round(cents * quantity);
}

/**
 * Desglose de una línea. El descuento se resta del bruto **antes** de separar el
 * impuesto: si el cliente paga menos, el IVA trasladado es el de lo que pagó.
 */
export function lineTotals(
  unitPriceCents: number,
  quantity: number,
  discountCents: number,
  tax: TaxSpec | null,
  pricesIncludeTax: boolean,
): LineTotals {
  const gross = multiplyCents(unitPriceCents, quantity) - discountCents;
  const rate = tax?.rate ?? 0;
  const included = pricesIncludeTax;

  let subtotal: number;
  let taxCents: number;

  if (rate > 0 && included) {
    subtotal = round(gross / (1 + rate));
    taxCents = gross - subtotal;
  } else if (rate > 0) {
    subtotal = gross;
    taxCents = round(gross * rate);
  } else {
    subtotal = gross;
    taxCents = 0;
  }

  return {
    quantity,
    unitPriceCents,
    discountCents,
    subtotalCents: subtotal,
    taxCents,
    totalCents: subtotal + taxCents,
  };
}

export function cartTotals(
  lines: LineTotals[],
  globalDiscountCents: number,
): CartTotals {
  const subtotal = lines.reduce((sum, line) => sum + line.subtotalCents, 0);
  const lineDiscounts = lines.reduce((sum, line) => sum + line.discountCents, 0);
  const tax = lines.reduce((sum, line) => sum + line.taxCents, 0);
  const total = Math.max(0, subtotal + tax - globalDiscountCents);

  return {
    subtotalCents: subtotal,
    discountCents: lineDiscounts + globalDiscountCents,
    taxCents: tax,
    totalCents: total,
    lineCount: lines.length,
    itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
  };
}

/** Cambio a devolver. Nunca negativo: si falta, es 0. */
export function changeFor(paidCents: number, totalCents: number): number {
  return Math.max(0, paidCents - totalCents);
}

/**
 * Cuánto falta por cubrir. Se usa para saber si el botón de cobro se habilita.
 */
export function missingFor(paidCents: number, totalCents: number): number {
  return Math.max(0, totalCents - paidCents);
}
